import { ComponentIgnoreOnPdf } from "@components/ComponentIgnoreOnPdf";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Box, Button, Chip, Typography, TextField } from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import { useState, useEffect, useMemo } from "react";
import { colors, alphaVar } from "../../../../../ui/colors";
import { ClientTotals } from "../../../../create/ClientPreview/components/ClientTotals";
import type { InvoiceStyleTokens } from "@components/invoice-customization/utils/createInvoiceStyleTokens";
import { PaymentType } from "../../../../create/PaymentMethods";
import { InvoiceCalculations } from "../../../../create/shared/types";
import { ClientPaymentSection } from "./ClientPaymentSection";
import { InvoiceClientTipCard } from "./InvoiceClientTipCard";

interface PaymentSectionProps {
  paymentStatus: boolean;
  handleChangeStatus?: any;
  onPayPalCaptureSuccess?: () => void;
  paymentMethods: any;
  paymentType: PaymentType;
  referenceNumber: string;
  canViewPayment: boolean;
  total: number;
  selectedPayments: string[];
  invoiceUid?: string;
  calculations: InvoiceCalculations;
  serviceFeeLabel: string;
  isDeposit?: boolean;
  balanceCoveredByDeposit?: boolean;
  depositCreditApplied?: number | null;
  depositCreditCurrency?: string;
  invoiceType?: string;
  isPdfMode?: boolean;
  invoiceDetails?: any;
  styleTokens?: InvoiceStyleTokens;
}

const isPayableClientProvider = (provider: string) =>
  provider === "paypal" || provider === "stripe" || provider === "bank_transfer";

export const PaymentSection = ({
  paymentStatus,
  handleChangeStatus,
  onPayPalCaptureSuccess,
  isPdfMode = false,
  paymentMethods,
  paymentType,
  referenceNumber,
  canViewPayment,
  total,
  selectedPayments,
  invoiceUid,
  calculations,
  serviceFeeLabel,
  isDeposit,
  balanceCoveredByDeposit,
  depositCreditApplied,
  depositCreditCurrency,
  invoiceType,
  invoiceDetails,
  styleTokens,
}: PaymentSectionProps) => {
  const isPaid = paymentStatus;
  const [payMethodsOpen, setPayMethodsOpen] = useState(false);
  const [customPayAmount, setCustomPayAmount] = useState<string>("");
  const [isCustomAmountMode, setIsCustomAmountMode] = useState(false);

  const isPartialPaymentEnabled = !!invoiceDetails?.allowPartialPayments;

  const completedPayments = useMemo(() => {
    return Array.isArray(invoiceDetails?.payments)
      ? invoiceDetails.payments.filter((p: any) => p.status?.toLowerCase() === "completed")
      : [];
  }, [invoiceDetails?.payments]);

  const totalPaidAmount = useMemo(() => {
    return completedPayments.reduce((sum: number, p: any) => sum + Number(p.amount || 0), 0);
  }, [completedPayments]);

  const tipAmount = Number(calculations.tipAmount ?? invoiceDetails?.tipAmount ?? invoiceDetails?.pricingDetail?.tip?.amount ?? 0);
  const remainingAmount = Math.max(
    0,
    Math.round((total + (Number.isFinite(tipAmount) && tipAmount > 0 ? tipAmount : 0) - totalPaidAmount) * 100) / 100,
  );

  const minPaymentConfig = Number(
    invoiceDetails?.pricingDetail?.minimumPaymentAmount ??
      invoiceDetails?.pricingDetails?.minimumPaymentAmount ??
      0
  );

  useEffect(() => {
    if (isPartialPaymentEnabled && remainingAmount > 0 && (!isCustomAmountMode || customPayAmount === "")) {
      setCustomPayAmount(remainingAmount.toFixed(2));
    }
  }, [isPartialPaymentEnabled, remainingAmount, isCustomAmountMode]);

  useEffect(() => {
    if (minPaymentConfig > 0 && remainingAmount < minPaymentConfig && isCustomAmountMode) {
      setIsCustomAmountMode(false);
    }
  }, [minPaymentConfig, remainingAmount, isCustomAmountMode]);

  const currency = invoiceDetails?.totalCost?.total?.currency || "USD";
  const formatMoney = (val: number) => {
    try {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency,
      }).format(val);
    } catch {
      return `$${val.toFixed(2)}`;
    }
  };

  const parsedCustomAmount = Number(customPayAmount);

  const paymentAmountToUse = isCustomAmountMode ? parsedCustomAmount : remainingAmount;

  const validationError = useMemo(() => {
    if (!isPartialPaymentEnabled) return "";
    if (!isCustomAmountMode) return "";
    if (customPayAmount === "") return "Please enter an amount to pay.";
    if (Number.isNaN(parsedCustomAmount) || parsedCustomAmount <= 0) {
      return "Please enter a valid positive payment amount.";
    }
    if (parsedCustomAmount > remainingAmount + 0.005) {
      return `Amount cannot exceed the remaining balance of ${formatMoney(remainingAmount)}.`;
    }
    if (minPaymentConfig > 0) {
      if (remainingAmount >= minPaymentConfig) {
        if (parsedCustomAmount < minPaymentConfig - 0.005) {
          return `Minimum payment amount is ${formatMoney(minPaymentConfig)}.`;
        }
      } else {
        if (parsedCustomAmount < remainingAmount - 0.005) {
          return `Please pay the full remaining balance of ${formatMoney(remainingAmount)}.`;
        }
      }
    }
    return "";
  }, [isPartialPaymentEnabled, isCustomAmountMode, customPayAmount, parsedCustomAmount, remainingAmount, minPaymentConfig]);

  const hasPayableClientMethods = (selectedPayments || []).some(isPayableClientProvider);
  // Hide Pay unless the invoice actually has a client payment method AND the viewer is Can Pay.
  const showPayCta = Boolean(canViewPayment && hasPayableClientMethods);

  const isSinglePaymentFullPaid = !isPartialPaymentEnabled || (isPaid && completedPayments.length <= 1);

  if (isSinglePaymentFullPaid) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%", minWidth: 0, borderRadius: 4 }}>
        {isPaid ? (
          <Box
            sx={{
              p: 2.5,
              bgcolor: alphaVar("#10b981", 0.04),
              border: `1px solid ${alphaVar("#10b981", 0.45)}`,
              borderRadius: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              shadow: "0px 4px 20px rgba(0,0,0,0.03)",
              gap: 1.5,
              flex: 1,
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                bgcolor: "#10b981",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: `0 4px 12px ${alphaVar("#10b981", 0.3)}`,
              }}
            >
              <CheckCircleIcon sx={{ color: "white", fontSize: 40 }} />
            </Box>
            <Box sx={{ textAlign: "center" }}>
              <Box sx={{ fontWeight: 900, color: "#10b981", fontSize: "1.25rem", mb: 0.5 }}>
                {balanceCoveredByDeposit ? "Saldo liquidado" : "Payment Completed"}
              </Box>
              <Box sx={{ color: colors.gray[500], fontSize: "0.875rem", maxWidth: 420, mx: "auto", lineHeight: 1.55 }}>
                {balanceCoveredByDeposit
                  ? "This invoice total was fully covered by deposit credit. No PayPal or card charge was generated, and no online payment is required."
                  : "Thank you for your payment. This invoice has been paid in full."}
              </Box>
              {balanceCoveredByDeposit && depositCreditApplied != null && Number(depositCreditApplied) > 0 && (
                <Box sx={{ color: colors.gray[600], fontSize: "0.875rem", fontWeight: 600 }}>
                  Deposit credit applied:{" "}
                  {Number(depositCreditApplied).toLocaleString("en-US", {
                    style: "currency",
                    currency: depositCreditCurrency || "USD",
                  })}
                </Box>
              )}
            </Box>
          </Box>
        ) : (
          <>
            <Box
              sx={{
                px: 0,
                display: "flex",
                flexDirection: "column",
                borderRadius: 4,
                shadow: "0px 4px 20px rgba(0,0,0,0.03)",
              }}
            >
              <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.5, justifyContent: "space-between" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: colors.gray[700] }}>
                    Payment status
                  </Typography>
                  <Chip size="small" label="Unpaid" color="warning" variant="outlined" sx={{ fontWeight: 700 }} />
                </Box>

                {!isPdfMode && !payMethodsOpen && showPayCta ? (
                  <Button variant="contained" color="primary" onClick={() => setPayMethodsOpen(true)} sx={{ textTransform: "none", fontWeight: 700 }}>
                    Pay
                  </Button>
                ) : null}
              </Box>

              {!isPdfMode && showPayCta && invoiceUid ? (
                <InvoiceClientTipCard
                  invoiceUid={invoiceUid}
                  invoiceTotal={Number(invoiceDetails?.totalCost?.total?.value ?? total)}
                  tipAmount={Number(calculations.tipAmount ?? invoiceDetails?.tipAmount ?? invoiceDetails?.pricingDetail?.tip?.amount ?? 0)}
                  tipKind={invoiceDetails?.pricingDetail?.tip?.kind}
                  tipValue={invoiceDetails?.pricingDetail?.tip?.value}
                />
              ) : null}

              {!isPdfMode && showPayCta && (
                <AnimatePresence mode="wait">
                  {payMethodsOpen ? (
                    <motion.div
                      key="pay-methods"
                      initial={{ opacity: 0, x: 28 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ type: "tween", duration: 0.28, ease: "easeOut" }}
                      style={{ width: "100%" }}
                    >
                      <ClientPaymentSection
                        handleChangeStatus={handleChangeStatus}
                        onPayPalCaptureSuccess={onPayPalCaptureSuccess}
                        paymentMethods={paymentMethods}
                        paymentType={paymentType}
                        referenceNumber={referenceNumber}
                        canViewPayment={canViewPayment}
                        total={remainingAmount}
                        paymentAmount={paymentAmountToUse}
                        selectedPayments={selectedPayments}
                        invoiceUid={invoiceUid}
                        invoiceType={invoiceType}
                      />
                    </motion.div>
                  ) : null}
                </AnimatePresence>
              )}
            </Box>

            <ClientTotals
              calculations={calculations}
              serviceFeeLabel={serviceFeeLabel}
              isDeposit={isDeposit}
              depositApplied={depositCreditApplied}
              styleTokens={styleTokens}
              invoiceDetails={invoiceDetails}
              isPdfMode={isPdfMode}
            />
          </>
        )}
      </Box>
    );
  }

  // Partial payment UI flow:
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, width: "100%", minWidth: 0, borderRadius: 4 }}>
      {remainingAmount <= 0.01 ? (
        // Completed Paid (Partial payments list)
        <Box
          sx={{
            p: 2.5,
            bgcolor: alphaVar("#10b981", 0.04),
            border: `1px solid ${alphaVar("#10b981", 0.45)}`,
            borderRadius: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            shadow: "0px 4px 20px rgba(0,0,0,0.03)",
            gap: 1.5,
            flex: 1,
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              bgcolor: "#10b981",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: `0 4px 12px ${alphaVar("#10b981", 0.3)}`,
            }}
          >
            <CheckCircleIcon sx={{ color: "white", fontSize: 40 }} />
          </Box>
          <Box sx={{ textAlign: "center", width: "100%" }}>
            <Box sx={{ fontWeight: 900, color: "#10b981", fontSize: "1.25rem", mb: 0.5 }}>
              Payment Completed
            </Box>
            <Box sx={{ color: colors.gray[500], fontSize: "0.875rem", maxWidth: 420, mx: "auto", mb: 3, lineHeight: 1.55 }}>
              Thank you for your payment. This invoice has been paid in full.
            </Box>
            
            {/* Payments list */}
            {completedPayments.length > 0 && (
              <Box sx={{ textAlign: "left", maxWidth: 420, mx: "auto", borderTop: `1px solid ${alphaVar(colors.gray[200], 0.5)}`, pt: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: colors.gray[700], mb: 1.5 }}>
                  Payment History
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {completedPayments.map((p: any, idx: number) => {
                    const payDate = p.paidAt
                      ? new Date(p.paidAt).toLocaleString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : "—";
                    return (
                      <Box
                        key={p.uid || idx}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          p: 1.25,
                          bgcolor: alphaVar(colors.gray[50], 0.5),
                          border: `1px solid ${alphaVar(colors.gray[200], 0.3)}`,
                          borderRadius: 1.5,
                          alignItems: "center"
                        }}
                      >
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 650, color: "text.primary", textTransform: "capitalize" }}>
                            {p.provider === "manual_entry" ? "Manual Entry" : p.provider}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "text.secondary" }}>
                            Paid on {payDate}
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 750, color: "#10b981" }}>
                          {formatMoney(Number(p.amount))}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      ) : (
        // Unpaid or Partially Paid (with outstanding balance)
        <>
          <Box
            sx={{
              px: 0,
              display: "flex",
              flexDirection: "column",
              borderRadius: 4,
              shadow: "0px 4px 20px rgba(0,0,0,0.03)",
            }}
          >
            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.5, justifyContent: "space-between" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: colors.gray[700] }}>
                  Payment status
                </Typography>
                <Chip
                  size="small"
                  label={completedPayments.length > 0 ? "Partially Paid" : "Unpaid"}
                  color="warning"
                  variant="outlined"
                  sx={{ fontWeight: 700 }}
                />
              </Box>

              {!isPdfMode && !payMethodsOpen && showPayCta ? (
                <Button
                  variant="contained"
                  color="primary"
                  disabled={!!validationError}
                  onClick={() => setPayMethodsOpen(true)}
                  sx={{ textTransform: "none", fontWeight: 700 }}
                >
                  Pay {!validationError ? formatMoney(paymentAmountToUse) : ""}
                </Button>
              ) : null}
            </Box>

              {!isPdfMode && showPayCta && invoiceUid ? (
                <InvoiceClientTipCard
                  invoiceUid={invoiceUid}
                  invoiceTotal={Number(invoiceDetails?.totalCost?.total?.value ?? total)}
                  tipAmount={Number(calculations.tipAmount ?? invoiceDetails?.tipAmount ?? invoiceDetails?.pricingDetail?.tip?.amount ?? 0)}
                  tipKind={invoiceDetails?.pricingDetail?.tip?.kind}
                  tipValue={invoiceDetails?.pricingDetail?.tip?.value}
                />
              ) : null}

            {/* Custom Payment Input Selection / Toggle */}
            {!isPdfMode && showPayCta && (minPaymentConfig === 0 || remainingAmount >= minPaymentConfig) && (
              <Box sx={{ mt: 2, mb: 1, p: 2, bgcolor: alphaVar(colors.gray[50], 0.3), border: `1px solid ${alphaVar(colors.gray[200], 0.5)}`, borderRadius: 2 }}>
                {!isCustomAmountMode ? (
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                    <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.85rem" }}>
                      This invoice accepts partial payments.
                    </Typography>
                    <Button
                      variant="text"
                      color="primary"
                      onClick={() => {
                        setIsCustomAmountMode(true);
                        setPayMethodsOpen(false);
                      }}
                      sx={{ textTransform: "none", fontWeight: 700, p: 0, "&:hover": { bgcolor: "transparent", textDecoration: "underline" } }}
                    >
                      Pay a custom amount
                    </Button>
                  </Box>
                ) : (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: colors.gray[700] }}>
                        How much would you like to pay now?
                      </Typography>
                      <Button
                        variant="text"
                        color="primary"
                        onClick={() => {
                          setIsCustomAmountMode(false);
                          setCustomPayAmount(remainingAmount.toFixed(2));
                          setPayMethodsOpen(false);
                        }}
                        sx={{ textTransform: "none", fontWeight: 700, p: 0, "&:hover": { bgcolor: "transparent", textDecoration: "underline" } }}
                      >
                        Pay full balance ({formatMoney(remainingAmount)})
                      </Button>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                      <TextField
                        variant="outlined"
                        size="small"
                        value={customPayAmount}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === "" || /^\d*\.?\d*$/.test(val)) {
                            setCustomPayAmount(val);
                          }
                        }}
                        placeholder={remainingAmount.toFixed(2)}
                        sx={{
                          maxWidth: 180,
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            backgroundColor: "background.paper",
                          },
                        }}
                        InputProps={{
                          startAdornment: <Box sx={{ mr: 0.5, color: "text.secondary", fontWeight: 700 }}>$</Box>,
                        }}
                      />
                      <Box>
                        {minPaymentConfig > 0 && remainingAmount >= minPaymentConfig && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                            * Minimum partial payment: <strong>{formatMoney(minPaymentConfig)}</strong>
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                          Remaining balance: <strong>{formatMoney(remainingAmount)}</strong>
                        </Typography>
                      </Box>
                    </Box>

                    {validationError && (
                      <Typography variant="caption" color="error" sx={{ display: "block", fontWeight: 600 }}>
                        {validationError}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            )}

            {!isPdfMode && showPayCta && (
              <AnimatePresence mode="wait">
                {payMethodsOpen && !validationError ? (
                  <motion.div
                    key="pay-methods"
                    initial={{ opacity: 0, x: 28 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ type: "tween", duration: 0.28, ease: "easeOut" }}
                    style={{ width: "100%", marginTop: "1rem" }}
                  >
                    <ClientPaymentSection
                      handleChangeStatus={handleChangeStatus}
                      onPayPalCaptureSuccess={onPayPalCaptureSuccess}
                      paymentMethods={paymentMethods}
                      paymentType={paymentType}
                      referenceNumber={referenceNumber}
                      canViewPayment={canViewPayment}
                      total={remainingAmount}
                      paymentAmount={paymentAmountToUse}
                      selectedPayments={selectedPayments}
                      invoiceUid={invoiceUid}
                      invoiceType={invoiceType}
                    />
                  </motion.div>
                ) : null}
              </AnimatePresence>
            )}

            {/* List of completed payments */}
            {completedPayments.length > 0 && (
              <Box sx={{ mt: 2.5, borderTop: `1px solid ${alphaVar(colors.gray[200], 0.5)}`, pt: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: colors.gray[700], mb: 1.5 }}>
                  Payment History
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {completedPayments.map((p: any, idx: number) => {
                    const payDate = p.paidAt
                      ? new Date(p.paidAt).toLocaleString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : "—";
                    return (
                      <Box
                        key={p.uid || idx}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          p: 1.25,
                          bgcolor: alphaVar(colors.gray[50], 0.5),
                          border: `1px solid ${alphaVar(colors.gray[200], 0.3)}`,
                          borderRadius: 1.5,
                          alignItems: "center"
                        }}
                      >
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 650, color: "text.primary", textTransform: "capitalize" }}>
                            {p.provider === "manual_entry" ? "Manual Entry" : p.provider}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "text.secondary" }}>
                            Paid on {payDate}
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 750, color: "#10b981" }}>
                          {formatMoney(Number(p.amount))}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}
          </Box>

          <ClientTotals
            calculations={calculations}
            serviceFeeLabel={serviceFeeLabel}
            isDeposit={isDeposit}
            depositApplied={depositCreditApplied}
            styleTokens={styleTokens}
            invoiceDetails={invoiceDetails}
            isPdfMode={isPdfMode}
          />
        </>
      )}
    </Box>
  );
};
